import styled, { css } from 'styled-components';
import { Button } from '@/components/UI/Button/Button';

const controlBase = css`
  white-space: nowrap;
  gap: 12px;
  height: 44px;
  min-height: 44px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.palette.secondary.border};
  background: ${({ theme }) => theme.palette.primary.background};
  color: ${({ theme }) => theme.palette.primary.labels};
  font-size: 14px;
  font-weight: 500;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.palette.brand.main};

    & path {
      fill: ${({ theme }) => theme.palette.brand.main};
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: auto;
    min-height: auto;
  }
`;

export const Control = styled(Button)<{
  $sortOpen?: boolean;
  $categoryOpen?: boolean;
  $categoryHasValue?: boolean;
}>`
  ${controlBase}

  ${({ $sortOpen }) =>
    typeof $sortOpen === 'boolean' &&
    css`
      position: relative;
      background-repeat: no-repeat;
      background-position: left 16px center;
      background-size: 20px;
      background-image: url(${$sortOpen
        ? '/images/shops/sort.svg'
        : '/images/shops/sort-closed.svg'});
      padding-left: 44px;
      padding-right: 44px;

      &::after {
        content: '';
        position: absolute;
        right: 16px;
        top: 50%;
        width: 15px;
        height: 15px;
        transform: translateY(-50%);
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        background-image: url(${$sortOpen
          ? '/images/shops/sort-icon.svg'
          : '/images/shops/sort-icon-closed.svg'});
      }
    `}

  ${({ $categoryOpen }) =>
    typeof $categoryOpen === 'boolean' &&
    css`
      position: relative;
      padding-right: 44px;

      &::after {
        content: '';
        position: absolute;
        right: 16px;
        top: 50%;
        width: 15px;
        height: 15px;
        transform: translateY(-50%);
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        background-image: url(${$categoryOpen
          ? '/images/shops/sort-icon.svg'
          : '/images/shops/sort-icon-closed.svg'});
      }
    `}

  ${({ $categoryHasValue }) =>
    $categoryHasValue &&
    css`
      &::after {
        display: none;
      }
      padding-right: 44px;

      & > svg:last-child {
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        margin: 0;
      }
    `}
`;

export const SortControlWrap = styled.div<{ $isActive?: boolean }>`
  position: relative;
  z-index: 2;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    & ${Control} {
      border: none;
      background: transparent;
      box-shadow: none;
      min-width: 0;

      &:hover {
        border: none;
        background: transparent;
      }
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-left: auto;

    ${({ $isActive, theme }) =>
      $isActive &&
      css`
        & ${Control} {
          border: 1px solid ${theme.palette.brand.main};
        }
      `}
  }
`;

export const SortDropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 999;
  width: 290px;
  height: 248px;
  padding: 10px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.palette.primary.border};
  background: ${({ theme }) => theme.palette.system.white};
  box-shadow: -4px 4px 15px 0 ${({ theme }) => theme.palette.overlay[8]},
    5px 5px 15px 0 ${({ theme }) => theme.palette.overlay[8]};
  font-size: 15px;
  font-weight: 400;
`;

export const SortMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const SortMenuItem = styled.button<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 44px;
  padding: 0 15px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: ${({ theme }) => theme.palette.primary.labels};
  font-size: 15px;
  font-weight: 400;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s ease;

  &:hover {
    background: ${({ theme }) => theme.palette.secondary.background};
  }

  ${({ $isActive, theme }) =>
    $isActive &&
    css`
      color: ${theme.palette.primary.labels};
    `}
`;

export const SortRadio = styled.span<{ $isActive?: boolean }>`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid
    ${({ theme, $isActive }) =>
      $isActive ? theme.palette.brand.main : theme.palette.secondary.border};
  background: ${({ theme }) => theme.palette.system.white};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ theme }) => theme.palette.brand.main};
    opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};
  }
`;

export const SortOptionButtonMobile = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 16px;
  font-size: 16px;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  border-bottom: 1px solid ${({ theme }) => theme.palette.secondary.border};
  color: ${({ theme }) => theme.palette.primary.labels};
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
`;

export const SortOptionRadioMobile = styled.div<{ $active: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid
    ${({ $active, theme }) =>
      $active ? theme.palette.brand.main : theme.palette.secondary.border};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${({ $active, theme }) =>
      $active ? theme.palette.brand.main : 'transparent'};
  }
`;
